import { useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery, useMutation } from "urql"
import { toast } from "sonner"
import { Pencil, ExternalLink, Copy, Check, KeyRound } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { PageHeader } from "@/components/shared/page-header"
import { StatusBadge } from "@/components/shared/status-badge"
import { TimeAgo } from "@/components/shared/time-ago"
import { CopyButton } from "@/components/shared/copy-button"
import { DataTable, type Column } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { FieldLabel, sectionTitleClass } from "@/components/shared/field-label"
import { ApplicationForm } from "../components/application-form"
import { ApplicationQuery, GeneratePortalTokenMutation } from "@/lib/graphql/outbound"
import { CreatePortalLinkMutation } from "@/lib/graphql/mutations"
import type { ResultOf } from "@/lib/graphql/types"

type EndpointItem = NonNullable<ResultOf<typeof ApplicationQuery>["application"]>["endpoints"][number]

const endpointColumns: Column<EndpointItem>[] = [
  {
    header: "URL",
    cell: (row) => (
      <code className="text-xs font-mono text-muted-foreground max-w-75 truncate block">
        {row.url}
      </code>
    ),
  },
  {
    header: "Status",
    cell: (row) => <StatusBadge status={row.status} />,
  },
  {
    header: "Timeout",
    cell: (row) => <span className="text-xs tabular-nums">{row.timeoutMs}ms</span>,
  },
  {
    header: "Rate Limit",
    cell: (row) => (
      <span className="text-xs text-muted-foreground tabular-nums">
        {row.rateLimitRps > 0 ? `${row.rateLimitRps} rps` : "unlimited"}
      </span>
    ),
  },
  {
    header: "Created",
    cell: (row) => <TimeAgo date={row.createdAt} />,
  },
]

export function ApplicationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [result, reexecute] = useQuery({ query: ApplicationQuery, variables: { id: id! } })
  const [editOpen, setEditOpen] = useState(false)
  const [portalOpen, setPortalOpen] = useState(false)
  const [tokenOpen, setTokenOpen] = useState(false)

  const app = result.data?.application

  if (result.fetching) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!app) return <EmptyState title="Application not found" />

  return (
    <div className="space-y-6">
      <PageHeader title={app.name}>
        <Button variant="outline" size="sm" onClick={() => setTokenOpen(true)}>
          <KeyRound className="h-3.5 w-3.5 mr-1.5" />
          Access Token
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPortalOpen(true)}>
          <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
          Portal Link
        </Button>
        <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
          <Pencil className="h-3.5 w-3.5 mr-1.5" />
          Edit
        </Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <FieldLabel className="mb-1">Name</FieldLabel>
                <div className="font-medium">{app.name}</div>
              </div>
              <div>
                <FieldLabel className="mb-1">UID</FieldLabel>
                <div className="flex items-center gap-1">
                  <code className="font-mono text-xs">{app.uid}</code>
                  <CopyButton text={app.uid} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-border/30">
          <CardHeader>
            <CardTitle className={sectionTitleClass}>Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <FieldLabel className="mb-1">ID</FieldLabel>
              <div className="flex items-center gap-1">
                <code className="font-mono text-xs">{app.id}</code>
                <CopyButton text={app.id} />
              </div>
            </div>
            <div>
              <FieldLabel className="mb-1">Created</FieldLabel>
              <TimeAgo date={app.createdAt} />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <h2 className="text-sm font-medium">Endpoints</h2>
        <DataTable
          columns={endpointColumns}
          data={app.endpoints}
          keyFn={(row) => row.id}
          emptyTitle="No endpoints"
          emptyDescription="Create an endpoint to deliver webhooks"
          onRowClick={(row) => navigate(`/endpoints/${row.id}`)}
        />
      </div>

      <ApplicationForm
        open={editOpen}
        onOpenChange={setEditOpen}
        onSuccess={() => reexecute({ requestPolicy: "network-only" })}
        initialData={app}
      />

      <PortalLinkDialog
        open={portalOpen}
        onOpenChange={setPortalOpen}
        applicationId={app.id}
      />

      <AccessTokenDialog
        open={tokenOpen}
        onOpenChange={setTokenOpen}
        applicationId={app.id}
      />
    </div>
  )
}

function PortalLinkDialog({
  open,
  onOpenChange,
  applicationId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  applicationId: string
}) {
  const [, createPortalLink] = useMutation(CreatePortalLinkMutation)
  const [expiresIn, setExpiresIn] = useState("24h")
  const [generatedUrl, setGeneratedUrl] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleGenerate = async () => {
    setSubmitting(true)
    const res = await createPortalLink({
      scope: { applicationId },
      expiresIn,
    })
    setSubmitting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    const token = res.data?.createPortalLink.token
    if (token) {
      setGeneratedUrl(`${window.location.origin}/portal?token=${token}`)
    }
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(generatedUrl)
    setCopied(true)
    toast.success("Portal link copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = (v: boolean) => {
    if (!v) {
      setGeneratedUrl("")
      setCopied(false)
    }
    onOpenChange(v)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Portal Link</DialogTitle>
        </DialogHeader>

        {!generatedUrl ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Create a managed portal link for your customer to view endpoints, messages, and delivery status. Portal links are tracked and can be revoked from the Portal Links page.
            </p>
            <div className="space-y-2">
              <Label>Expires In</Label>
              <Select value={expiresIn} onValueChange={setExpiresIn}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">1 hour</SelectItem>
                  <SelectItem value="24h">24 hours</SelectItem>
                  <SelectItem value="168h">7 days</SelectItem>
                  <SelectItem value="720h">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={submitting}>
                {submitting ? "Generating..." : "Generate"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Share this link with your customer. It will expire in {expiresIn}.
            </p>
            <div className="flex items-center gap-2">
              <Input
                readOnly
                value={generatedUrl}
                className="text-xs font-mono"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button variant="outline" size="icon" className="shrink-0" onClick={handleCopy}>
                {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Done
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(generatedUrl, "_blank")}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open Portal
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

function AccessTokenDialog({
  open,
  onOpenChange,
  applicationId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  applicationId: string
}) {
  const [, generateToken] = useMutation(GeneratePortalTokenMutation)
  const [token, setToken] = useState("")
  const [expiresAt, setExpiresAt] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const [copied, setCopied] = useState(false)

  const handleGenerate = async () => {
    setSubmitting(true)
    const res = await generateToken({ applicationId })
    setSubmitting(false)
    if (res.error) {
      toast.error(res.error.message)
      return
    }
    if (res.data?.generatePortalToken) {
      setToken(res.data.generatePortalToken.token)
      setExpiresAt(res.data.generatePortalToken.expiresAt)
    }
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(token)
    setCopied(true)
    toast.success("Token copied to clipboard")
    setTimeout(() => setCopied(false), 2000)
  }

  const handleClose = (v: boolean) => {
    if (!v) {
      setToken("")
      setExpiresAt("")
      setCopied(false)
    }
    onOpenChange(v)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Generate Access Token</DialogTitle>
        </DialogHeader>

        {!token ? (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Generate a short-lived access token for this application. Use this for programmatic API access or embedding the portal. The token is ephemeral and not tracked as a portal link.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancel
              </Button>
              <Button onClick={handleGenerate} disabled={submitting}>
                {submitting ? "Generating..." : "Generate Token"}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              This token grants portal access for this application. Expires{" "}
              <time dateTime={expiresAt} className="font-medium text-foreground">
                {new Date(expiresAt).toLocaleString()}
              </time>.
            </p>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Token</Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={token}
                  className="text-xs font-mono"
                  onClick={(e) => e.currentTarget.select()}
                />
                <Button variant="outline" size="icon" className="shrink-0" onClick={handleCopy}>
                  {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)}>
                Done
              </Button>
              <Button
                variant="outline"
                onClick={() => window.open(`${window.location.origin}/portal?token=${token}`, "_blank")}
              >
                <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                Open Portal
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
